import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts'
import { getStats } from 'services/shipments.service'
import TrialBanner from 'components/TrialBanner'
import useUsage from 'hooks/useUsage'
import { useI18n } from 'i18n'

// First run: what to do with an empty account.
const GettingStarted = () => {
  const { t } = useI18n()
  const steps = ['create', 'upload', 'review']
  return (
    <section className='rounded-xl border border-slate-200 bg-white p-5 sm:p-6'>
      <h2 className='text-base font-semibold text-slate-800'>{t('onboarding.title')}</h2>
      <p className='mt-1 text-sm text-slate-500'>{t('onboarding.subtitle')}</p>
      <ol className='mt-5 grid gap-4 md:grid-cols-3'>
        {steps.map((step, i) => (
          <li key={step} className='flex gap-3'>
            <span className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand/10 text-sm font-bold text-brand'>{i + 1}</span>
            <div>
              <p className='text-sm font-semibold text-slate-800'>{t(`onboarding.${step}Title`)}</p>
              <p className='mt-0.5 text-sm text-slate-500'>{t(`onboarding.${step}Body`)}</p>
            </div>
          </li>
        ))}
      </ol>
      <Link to='/shipments' className='mt-6 inline-block rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-ink hover:bg-accent-600'>
        {t('onboarding.cta')}
      </Link>
    </section>
  )
}

const StatCard = ({ label, value, tone }) => (
  <div className='rounded-xl border border-slate-200 bg-white p-5'>
    <p className='text-sm text-slate-500'>{label}</p>
    <p className={`mt-1 text-3xl font-bold ${tone || 'text-slate-800'}`}>{value}</p>
  </div>
)

const ShareTooltip = ({ active, payload, label }) => {
  const { t, fmt } = useI18n()
  if (!active || !payload?.length) return null
  return (
    <div className='rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs shadow-sm'>
      <p className='font-semibold text-slate-800'>{label}</p>
      <p className='text-slate-600'>{t('dashboard.ofLanded', { value: fmt.number(payload[0].value, 2) })}</p>
    </div>
  )
}

const Dashboard = () => {
  const { t, fmt } = useI18n()
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const { usage } = useUsage()

  useEffect(() => {
    (async () => {
      try {
        setStats(await getStats())
      } catch (error) {
        toast.error(t('dashboard.loadFailed'))
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  if (loading) return <p className='text-slate-400'>{t('common.loading')}</p>
  if (!stats) return <p className='text-slate-400'>{t('common.noData')}</p>

  const shares = (stats.layerShare || []).filter((l) => l.share > 0).map((l) => ({ ...l, label: t(`layers.${l.layer}`) }))

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-2xl font-bold'>{t('dashboard.title')}</h1>
        <p className='text-sm text-slate-500'>{t('dashboard.subtitle')}</p>
      </div>

      <TrialBanner usage={usage} />
      {stats.total === 0 && !usage?.reached && <GettingStarted />}

      <div className='grid grid-cols-2 gap-4 md:grid-cols-4'>
        <StatCard label={t('dashboard.shipments')} value={stats.total} />
        <StatCard label={t('dashboard.calculated')} value={stats.calculated} tone='text-status-ready' />
        <StatCard label={t('dashboard.toReview')} value={stats.pendingLines} tone={stats.pendingLines ? 'text-status-review' : undefined} />
        <StatCard label={t('dashboard.documents')} value={stats.documents} />
      </div>

      <div className='rounded-xl border border-slate-200 bg-white p-5'>
        <h2 className='mb-1 text-sm font-semibold text-slate-700'>{t('dashboard.whereTitle')}</h2>
        <p className='mb-4 text-xs text-slate-500'>{t('dashboard.whereSubtitle')}</p>
        {shares.length === 0 && (
          <p className='py-8 text-center text-slate-400'>
            {t('dashboard.noneCalculated')} <Link to='/shipments' className='font-semibold text-brand'>{t('dashboard.goToShipments')}</Link>
          </p>
        )}
        {shares.length > 0 && (
          <div style={{ width: '100%', height: shares.length * 40 + 40 }}>
            <ResponsiveContainer>
              <BarChart data={shares} layout='vertical' margin={{ top: 4, right: 48, left: 8, bottom: 4 }} barCategoryGap='30%'>
                <CartesianGrid strokeDasharray='3 3' horizontal={false} stroke='#e2e8f0' />
                <XAxis type='number' unit='%' tick={{ fontSize: 12, fill: '#64748b' }} />
                <YAxis type='category' dataKey='label' width={170} tick={{ fontSize: 12, fill: '#334155' }} />
                <Tooltip content={<ShareTooltip />} cursor={{ fill: 'rgba(148,163,184,0.12)' }} />
                <Bar dataKey='share' fill='#2a78d6' radius={[0, 4, 4, 0]}
                  label={{ position: 'right', fontSize: 11, fill: '#475569', formatter: (v) => `${fmt.number(v, 2)}%` }} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  )
}

export default Dashboard
